import React, { useEffect, useMemo, useState } from 'react';
import RiskGauge from './RiskGauge.jsx';
import LoadingIndicator from "../common/loading_indicator.jsx";
import { risk_management_data_path } from '~/routes';

const RiskMatrix = ({
  data: dataProp, 
  section, 
  filterModel, 
  setFilterModel, 
  detailedView, 
  projectId,
  renderGauge = true //TBD: Bad prop, would be better to handle this with composition
}) => {
  const [fetchedData, setFetchedData] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (!dataProp && projectId) {
      jQuery.getJSON(risk_management_data_path({project_id: projectId}))
        .success((res) => {
          setFetchedData(res);
        })
        .error(() => {
          setErrorMsg(I18n.t('invalid_data'));
        });
    }
  }, [projectId]);

  const data = dataProp || fetchedData;

  const {
    risk_matrix_dimensions: dimensions,
    risk_matrix_risk_thresholds: risk_thresholds,
    risk_datagrid_accessor_map: accessor_map,
    risk_matrix_section_valid,
    risk_matrix_section_complete,
    risk_average_score,
    risk_count,
    risk_count_map,
    context
  } = data || {};

  const {likelihood, severity} = dimensions || {};

  const errorLabel = useMemo(() => {
    if (!risk_matrix_section_valid) {
      return I18n.t('configuration_invalid')
    } else if (!risk_matrix_section_complete) {
      return I18n.t('configuration_incomplete')
    }
    return null;
  }, [risk_matrix_section_complete, risk_matrix_section_valid]);

  const currentFilter = useMemo(() => {
    if (!filterModel || errorLabel) return {};
    const severityAccessor = accessor_map?.severity;
    const likelihoodAccessor = accessor_map?.likelihood;
    if (filterModel[severityAccessor]?.values && filterModel[likelihoodAccessor]?.values) {
      return {severity: Number(filterModel[severityAccessor].values[0]), likelihood: Number(filterModel[likelihoodAccessor].values[0])};
    }
  }, [filterModel, accessor_map]);

  if (detailedView) return null;

  if (!data && !errorMsg) return <LoadingIndicator />;
  if (errorMsg) return <div className='error-state'>{errorMsg}</div>;

  const updateFilterModel = (severity, likelihood) => {
    const severityAccessor = accessor_map?.severity;
    const likelihoodAccessor = accessor_map?.likelihood;

    if (!severityAccessor || !likelihoodAccessor) {
      return;
    }

    if (currentFilter?.severity === severity && currentFilter?.likelihood === likelihood) {
      setFilterModel({});
      return;
    }
    setFilterModel({
      [severityAccessor]: {values: [String(severity)], filterType: 'set'},
      [likelihoodAccessor]: {values: [String(likelihood)], filterType: 'set'}
    });
  };

  const getThresholdObj = value => {
    let thresholdObj = null;
    risk_thresholds.forEach(t => {
      if (value >= t.value && value > (thresholdObj?.value || 0)) {
        thresholdObj = t;
      }
    });
    return thresholdObj || {value: 0, label: 'very_low', color: '#57d492'};
  };

  const verticalLevels = errorLabel ? [] : [...(likelihood || [])].reverse();
  const diagonalLevels = errorLabel ? [] : (severity || []);

  return (
    <>
      {section?.section_name && <h3 className='mt-0'>{section.section_name[I18n.locale]}</h3>}
      {!errorLabel && <div className="risk-matrix-section">
        <div className="matrix-container">
          {verticalLevels.map(v => (
            <div key={v.value} className="matrix-row">
              <div className="axis-label vertical-label">
                <span>{v.value}</span> <span className=''>{v.label}</span>
              </div>
              {diagonalLevels.map(d => {
                const product = v.value * d.value;
                const count = risk_count_map?.[`${v.value}-${d.value}`] || 0;
                const { color } = getThresholdObj(product);
                const isCurrent = currentFilter && currentFilter.likelihood === v.value && currentFilter.severity === d.value;
                return (
                  <div key={d.value}
                    className={`cell ${isCurrent ? 'current' : ''} ${count === 0 ? 'empty' : ''}`}
                    style={{ backgroundColor: color, ...(count === 0 ? {color: '#FFF'} : {}) }}
                    onClick={() => updateFilterModel(d.value, v.value)}
                  >
                    {count === 0 ? '' : `${count} ${count === 1 ? I18n.t('risk') : I18n.t('risks')}`}
                  </div>
                );
              })}
            </div>
          ))}
          <div className="matrix-row horizontal-label-row">
            <div className="corner"></div>
            {diagonalLevels.map(d => (
              <div key={d.value} className="axis-label horizontal-label">
                {d.value} {d.label}
              </div>
            ))}
          </div>
          <div className="risk-matrix-description">
            <label>{I18n.t('risk_matrix')}:</label>
            {I18n.t('risk_matrix_description_with_count', {count: risk_count})}
          </div>
        </div>
        {renderGauge && <RiskGauge risk_count={risk_count} risk_average_score={risk_average_score} dimensions={dimensions} risk_thresholds={risk_thresholds} project_id={projectId} context={context} />}
      </div>}
      {errorLabel && <p className='description'>{errorLabel}</p>}
    </>
  );
}

export default RiskMatrix;
